<template>
  <div>
    <b-form
      inline
      class="mb-2"
    >
      <b-button
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        variant="primary"
        class="d-inline-block mr-1 mb-1"
        @click="createNew"
      >
        {{ $t('Add') }}

      </b-button>

      <b-form-input
        v-model="searchTerm"
        :placeholder=" $t('message.SearchPlaceholder')"
        type="text"
        class="d-inline mb-1"
      />

      <b-dropdown
        v-if="selectedRecords.length>0"
        id="dropdown-actions"
        v-ripple.400="'rgba(255, 255, 255, 0.15)'"
        :text="$t('Actions')"
        variant="primary"
        class="ml-1 mb-1"
      >
        <b-dropdown-item
          variant="success"
          @click="setStatusSelectedRecords(true)"
        >
          <feather-icon
            icon="EyeIcon"
          />
          {{ $t('Set as Active') }}

        </b-dropdown-item>
        <b-dropdown-item
          variant="warning"
          @click="setStatusSelectedRecords(false)"
        >
          <feather-icon
            icon="EyeOffIcon"
          />
          {{ $t('Set as Passive') }}

        </b-dropdown-item>
        <b-dropdown-divider />
        <b-dropdown-item
          variant="danger"
          @click="confirmDelete"
        >
          <feather-icon
            icon="Trash2Icon"
          />
          {{ $t('Delete') }}

        </b-dropdown-item>
      </b-dropdown>

    </b-form>

    <!-- table -->
    <vue-good-table
      style-class="vgt-table striped"
      :columns="columns"
      :rows="data"
      :sort-options="{
        enabled: true,
        initialSortBy: {field: 'id', type: 'desc'}
      }"
      :search-options="{
        enabled: true,
        externalQuery: searchTerm }"
      :select-options="{
        enabled: true,
        selectOnCheckboxOnly: true, // only select when checkbox is clicked instead of the row
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true, // disable the select info panel on top
      }"
      :pagination-options="{
        enabled: true,
        perPage:pageLength,
        mode: 'records',
        perPage: 10,
        perPageDropdown: [10, 50, 100],
        dropdownAllowAll: false,
      }"
      @on-selected-rows-change="selectedRowsChanged"
    >

      <template
        slot="table-column"
        slot-scope="props"
      >
        <span>
          {{ $t(props.column.label) }}
        </span>
      </template>

      <template
        slot="table-row"
        slot-scope="props"
      >

        <!-- Column: id -->
        <span
          v-if="props.column.field === 'id'"
          class="text-nowrap"
        >
          <b-link @click="edit(props.row)">
            <feather-icon
              icon="EditIcon"
            />

          </b-link>
        </span>
        <span
          v-else-if="props.column.field === 'pageIcon'"
          class="text-nowrap"
        >
          <feather-icon
            :icon="props.row[props.column.field]||'CircleIcon'"
          />
        </span>
        <span
          v-else-if="props.column.field === 'status'||props.column.field === 'isPrimary'"
          class="text-nowrap"
        >
          <b-badge
            pill
            :variant="`light-${resolveStatus(props.row[props.column.field])}`"
            class="text-capitalize"
          >
            {{ props.row[props.column.field] }}
          </b-badge>
        </span>
        <span
          v-else-if="props.column.field === 'filterConfigJson'"
          class="text-nowrap"
        >
          <div v-if="props.row.filterConfig && props.row.filterConfig.filters">
            <div
              v-for="(filterItem,index) in props.row.filterConfig.filters"
              :key="index"
            >
              <template v-if="filterItem.operator">
                {{ filterItem.operator }}
              </template>
              <template v-else>
                <strong class="mr-1">{{ filterItem.filterQuestion.questionText||filterItem.filterQuestion.questionName }} = </strong>
                <b-badge
                  v-for="(value,idx) in filterItem.filterValues"
                  :key="idx"
                  variant="primary"
                >{{ value.optionText||value.optionValue }}</b-badge>
                <b-badge
                  v-for="(value,idx) in filterItem.filterValuesText"
                  :key="idx"
                  variant="primary"
                >{{ value }}</b-badge>
              </template>
            </div>
          </div>
        </span>

        <!-- Column: Common -->
        <span v-else>
          {{ props.formattedRow[props.column.field] }}
        </span>
      </template>

    </vue-good-table>

    <b-modal
      id="add-user-hot-alert-config"
      :no-close-on-backdrop="true"
      :no-close-on-esc="false"
      :title="$t('Hot Alert Config')"
      :ok-title="$t('OK')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      size="lg"
      @ok="handleOk"
      @cancel="handleCancel"
    >
      <form-user-hot-alert-config
        ref="form-user-hot-alert-config"
        v-model="edit_item"
        :user-id="userId"
        @submit="getUserProjects"
      />
    </b-modal>

  </div>
</template>

<script>
import useJwt from '@/auth/jwt/useJwt'

import {
  BLink, BFormInput, BForm, BButton, BDropdown, BDropdownItem, BDropdownDivider, BBadge,
} from 'bootstrap-vue'
import Ripple from 'vue-ripple-directive'
import { VueGoodTable } from 'vue-good-table'
import FormUserHotAlertConfig from '@/layouts/components/forms/FormUserHotAlertConfig.vue'

export default {
  components: {
    FormUserHotAlertConfig,
    VueGoodTable,
    BLink,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
    BFormInput,
    BForm,
    BButton,
    BBadge,
  },
  directives: {
    Ripple,
  },
  props: {
    userId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      data: [],
      columns: [
        {
          label: '#',
          field: 'id',
          type: 'number',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
        },
        {
          label: '',
          field: 'icon',
          tdClass: 'text-center',
          thClass: 'text-center',
          width: '50px',
          sortable: false,
        },
        {
          label: 'Project',
          field: 'project.projectName',
        },
        {
          label: 'Filter',
          field: 'filterConfigJson',
        },
        {
          label: 'Status',
          field: 'status',
        },
        {
          label: 'Primary',
          field: 'isPrimary',
        },
      ],
      pageLength: 10,
      searchTerm: '',
      selectedRecords: [],
      edit_item: null,
    }
  },
  created() {
    this.getUserProjects()
  },
  methods: {
    resolveStatus(status) {
      if (status) return 'success'
      if (!status) return 'danger'
      return 'secondary'
    },
    getUserProjects() {
      this.edit_item = null
      this.$bvModal.hide('add-user-hot-alert-config')
      useJwt.axiosIns.get(`user/${this.userId}/userHotAlertConfig`)
        .then(response => {
          if (response.data) {
            this.data = response.data
            this.data.forEach(element => {
              const el = element
              if (el.filterConfigJson) {
                el.filterConfig = JSON.parse(element.filterConfigJson)
              }
            })
          }
        })
        .catch(e => {
          console.log(e)
        })
    },
    edit(item) {
      this.edit_item = JSON.parse(JSON.stringify(item))
      this.$bvModal.show('add-user-hot-alert-config')
    },
    createNew() {
      this.$bvModal.show('add-user-hot-alert-config')
    },
    selectedRowsChanged(vals) {
      this.selectedRecords = vals.selectedRows.map(x => x.id)
    },

    confirmDelete() {
      this.$swal({
        title: 'Are you sure?',
        text: `You are about to delete ${this.selectedRecords.length} record(s)...`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete it!',
        customClass: {
          confirmButton: 'btn btn-primary',
          cancelButton: 'btn btn-outline-danger ml-1',
        },
        buttonsStyling: false,
      }).then(result => {
        if (result.value) {
          this.deleteSelectedRecords()
        }
      })
    },

    deleteSelectedRecords() {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.delete(
        `user/userHotAlertConfig/${x}`,
      ))

      Promise.all(promises).then(() => {
        this.getUserProjects()
        // show message
        this.$swal({
          icon: 'success',
          title: 'Deleted!',
          text: `${this.selectedRecords.length} record(s) has been deleted.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    setStatusSelectedRecords(status) {
      const promises = this.selectedRecords.map(x => useJwt.axiosIns.patch(
        `user/userHotAlertConfig/${x}/setStatus/${status}`,
      ))

      Promise.all(promises).then(() => {
        this.getUserProjects()
        // show message
        this.$swal({
          icon: 'success',
          title: 'Success!',
          text: `${this.selectedRecords.length} record(s) has been set status:${status}.`,
          customClass: {
            confirmButton: 'btn btn-success',
          },
        })
      })
    },
    handleOk(bvModalEvt) {
      // Prevent modal from closing
      bvModalEvt.preventDefault()
      // Trigger submit handler
      this.$refs['form-user-hot-alert-config'].validationForm()
    },
    handleCancel() {
      this.edit_item = null
    },

  },
}
</script>

<style lang="scss" >
@import '@core/scss/vue/libs/vue-good-table.scss';
</style>
